import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { PriorityBadge } from "@/components/Badges";
import { PRIORITIES, timeAgo, VEHICLE_STATUSES } from "@/lib/constants";
import { Plus, Building2, Truck, X, Loader2, ArrowUpRight, MapPin, Search, Settings, Trash2, AlertTriangle, CheckCheck } from "lucide-react";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { LocationPicker, IncidentMiniMap } from "@/components/IncidentMaps";
import { confirmDialog } from "@/components/Confirm";
import { toast } from "sonner";

function CreateDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ title: "", incident_type_id: "", building_id: "", priority: 2, description: "" });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [vehLocations, setVehLocations] = useState([]);
  const [vehQ, setVehQ] = useState("");
  const [vehLoc, setVehLoc] = useState("");
  const [saving, setSaving] = useState(false);
  const [locMode, setLocMode] = useState("building"); // building | map
  const [bq, setBq] = useState("");
  const [pin, setPin] = useState(null); // {lat,lng}
  const [pinLabel, setPinLabel] = useState("");
  const [titleSug, setTitleSug] = useState([]);
  const [manageTitles, setManageTitles] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTitleType, setNewTitleType] = useState("");
  const [bulkTitles, setBulkTitles] = useState("");
  const [caEnabled, setCaEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/incident-types"), api.get("/buildings"), api.get("/vehicles"), api.get("/incident-titles")]).then(([t, b, v, ti]) => {
      setTypes(t.data); setBuildings(b.data); setVehicles(v.data); setTitleSug(ti.data || []);
      api.get("/locations").then(({ data }) => setVehLocations(data || [])).catch(() => {});
      setNewTitleType(t.data[0]?.id || "");
      setForm((f) => ({ ...f, incident_type_id: t.data[0]?.id || "", building_id: b.data[0]?.id || "" }));
    });
    api.get("/settings/callassist").then(({ data }) => setCaEnabled(!!data.enabled)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const toggleVehicle = (id) =>
    setSelectedVehicles((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (locMode === "building" && !form.building_id) { toast.error(t("inc.pickBuildingErr")); return; }
    if (locMode === "map" && !pin) { toast.error(t("inc.setPinErr")); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, incident_type_id: form.incident_type_id,
        priority: Number(form.priority), description: form.description, vehicle_ids: selectedVehicles,
        building_id: locMode === "building" ? form.building_id : null,
        location_lat: locMode === "map" ? pin.lat : null,
        location_lng: locMode === "map" ? pin.lng : null,
        location_label: locMode === "map" ? (pinLabel || t("inc.mapLocation")) : null,
      };
      const { data } = await api.post("/incidents", payload);
      toast.success(t("inc.created", { ref: data.ref }));
      onCreated(data);
      onClose();
      navigate(`/incidenten/${data.id}`, { state: { openCallAssist: caEnabled } });
      setForm({ title: "", incident_type_id: types[0]?.id || "", building_id: buildings[0]?.id || "", priority: 2, description: "" });
      setSelectedVehicles([]); setPin(null); setPinLabel(""); setBq(""); setLocMode("building");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reloadTitles = () => api.get("/incident-titles").then(({ data }) => setTitleSug(data || []));
  const onTitleChange = (val) => setForm((f) => {
    const m = titleSug.find((t) => t.value.toLowerCase() === val.trim().toLowerCase());
    return { ...f, title: val, incident_type_id: m && m.incident_type_id ? m.incident_type_id : f.incident_type_id };
  });
  const addTitle = async () => {
    const vals = bulkTitles.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload = vals.length ? { values: vals, incident_type_id: newTitleType || null } : { value: newTitle.trim(), incident_type_id: newTitleType || null };
    if (!payload.value && !(payload.values && payload.values.length)) { toast.error(t("inc.enterTitleErr")); return; }
    try { await api.post("/incident-titles", payload); setNewTitle(""); setBulkTitles(""); await reloadTitles(); toast.success(t("inc.titlesAdded")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const updateTitleType = async (t, typeId) => { try { await api.put(`/incident-titles/${t.id}`, { incident_type_id: typeId || null }); await reloadTitles(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const updateTitleFlag = async (t, val) => { try { await api.put(`/incident-titles/${t.id}`, { tweezijdig_aanrijden: val }); await reloadTitles(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const deleteTitle = async (t) => { try { await api.delete(`/incident-titles/${t.id}`); await reloadTitles(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  const locMap = Object.fromEntries((vehLocations || []).map((l) => [l.id, l.name]));
  const shownVehicles = vehicles.filter((v) => {
    if (vehLoc && v.location_id !== vehLoc) return false;
    if (vehQ.trim()) return `${v.callsign} ${v.name || ""} ${v.type || ""}`.toLowerCase().includes(vehQ.trim().toLowerCase());
    return true;
  });
  const matchedTitle = titleSug.find((t) => t.value.toLowerCase() === form.title.trim().toLowerCase());
  const shownBuildings = buildings.filter((b) => !bq || (`${b.name} ${b.function || ""} ${b.code || ""}`).toLowerCase().includes(bq.toLowerCase()));
  const selBuilding = buildings.find((b) => b.id === form.building_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div data-testid="create-incident-dialog" className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-display text-lg font-bold text-slate-900">{t("inc.newIncident")}</h3>
          <button data-testid="close-dialog" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("inc.typeLabel")}</label>
            <select data-testid="incident-type" value={form.incident_type_id} onChange={(e) => setForm({ ...form, incident_type_id: e.target.value, title: "" })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 md:max-w-xs">
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("inc.titleLabel")}</label>
              <button type="button" data-testid="manage-titles-toggle" onClick={() => setManageTitles((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700">
                <Settings className="h-3 w-3" /> {t("inc.manageTitles")}
              </button>
            </div>
            <input data-testid="incident-title" required value={form.title} onChange={(e) => onTitleChange(e.target.value)}
              list="incident-title-suggestions" autoComplete="off"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder={t("inc.titlePh")} />
            <datalist id="incident-title-suggestions">{titleSug.filter((ti) => !ti.incident_type_id || ti.incident_type_id === form.incident_type_id).map((t) => <option key={t.id} value={t.value} />)}</datalist>

            {manageTitles && (
              <div data-testid="titles-manager" className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("inc.manageHeader")}</div>
                <div className="mb-2 flex flex-wrap items-end gap-2">
                  <input data-testid="new-title-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder={t("inc.newTitlePh")}
                    className="min-w-[140px] flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-blue-500" />
                  <select data-testid="new-title-type" value={newTitleType} onChange={(e) => setNewTitleType(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-blue-500">
                    <option value="">{t("inc.linkType")}</option>
                    {types.map((t2) => <option key={t2.id} value={t2.id}>{t2.name}</option>)}
                  </select>
                  <button type="button" data-testid="add-title-btn" onClick={addTitle}
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">
                    <Plus className="h-3.5 w-3.5" /> {t("inc.add")}
                  </button>
                </div>
                <textarea data-testid="bulk-titles-input" value={bulkTitles} onChange={(e) => setBulkTitles(e.target.value)} rows={2}
                  placeholder={t("inc.bulkPh")}
                  className="mb-2 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs outline-none focus:border-blue-500" />
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {titleSug.map((ti) => (
                    <div key={ti.id} data-testid={`title-row-${ti.id}`} className="flex items-center gap-2 rounded-lg bg-white px-2 py-1.5">
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{ti.value}</span>
                      <select data-testid={`title-type-${ti.id}`} value={ti.incident_type_id || ""} onChange={(e) => updateTitleType(ti, e.target.value)}
                        className="max-w-[130px] rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-600 outline-none focus:border-blue-500">
                        <option value="">{t("inc.noType")}</option>
                        {types.map((ty) => <option key={ty.id} value={ty.id}>{ty.name}</option>)}
                      </select>
                      <button type="button" data-testid={`title-tweezijdig-${ti.id}`} onClick={() => updateTitleFlag(ti, !ti.tweezijdig_aanrijden)}
                        title="Tweezijdig aanrijden"
                        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] font-semibold ${ti.tweezijdig_aanrijden ? "border-red-300 bg-red-50 text-red-600" : "border-slate-200 bg-white text-slate-400 hover:text-slate-600"}`}>
                        <AlertTriangle className="h-3 w-3" /> {t("inc.twoSided")}
                      </button>
                      <button type="button" data-testid={`delete-title-${ti.id}`} onClick={() => deleteTitle(ti)}
                        className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Location: building search OR map pin */}
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("inc.locLabel")}</label>
              <div className="flex gap-1">
                <button type="button" data-testid="locmode-building" onClick={() => setLocMode("building")} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${locMode === "building" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{t("inc.building")}</button>
                <button type="button" data-testid="locmode-map" onClick={() => setLocMode("map")} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold ${locMode === "map" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}><MapPin className="h-3.5 w-3.5" /> {t("inc.mapPin")}</button>
              </div>
            </div>

            {locMode === "building" ? (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-2">
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <input data-testid="building-search-input" autoFocus value={bq} onChange={(e) => setBq(e.target.value)} placeholder={t("inc.buildingSearchPh")}
                      className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm outline-none focus:border-blue-500" />
                  </div>
                  <div className="max-h-56 space-y-1 overflow-y-auto">
                    {shownBuildings.length === 0 && <div className="py-3 text-center text-xs text-slate-400">{t("inc.noBuildings")}</div>}
                    {shownBuildings.map((b) => (
                      <button key={b.id} type="button" data-testid={`building-opt-${b.id}`} onClick={() => setForm({ ...form, building_id: b.id })}
                        className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${form.building_id === b.id ? "bg-blue-50 ring-1 ring-blue-500" : "hover:bg-slate-50"}`}>
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-blue-600 text-[10px] font-bold text-white">{b.code}</div>
                        <div className="min-w-0"><div className="truncate font-medium text-slate-900">{b.name}</div>{b.function && <div className="truncate text-[11px] text-blue-600">{b.function}</div>}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-2" data-testid="building-preview-map">
                  {selBuilding ? (
                    <>
                      <div className="mb-1.5 px-1 text-xs font-semibold text-slate-700">{selBuilding.name}{selBuilding.code ? ` · ${selBuilding.code}` : ""}</div>
                      <IncidentMiniMap key={selBuilding.id} lat={selBuilding.lat} lng={selBuilding.lng} label={selBuilding.name} />
                    </>
                  ) : <div className="flex h-48 items-center justify-center text-center text-xs text-slate-400">{t("inc.pickBuilding")}</div>}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <LocationPicker value={pin} onPick={setPin} buildings={buildings} />
                <div className="flex items-center gap-2">
                  <input data-testid="pin-label" value={pinLabel} onChange={(e) => setPinLabel(e.target.value)} placeholder={t("inc.pinLabelPh")}
                    className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                  <span className="whitespace-nowrap text-xs text-slate-400">{pin ? `${pin.lat}, ${pin.lng}` : t("inc.clickMap")}</span>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("inc.priority")}</label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((p) => (
                <button key={p} type="button" data-testid={`priority-option-${p}`} onClick={() => setForm({ ...form, priority: p })}
                  className={`rounded-xl border-2 px-2 py-2 text-xs font-semibold transition-all ${form.priority === p ? "" : "opacity-50"}`}
                  style={{ backgroundColor: PRIORITIES[p].bg, borderColor: form.priority === p ? PRIORITIES[p].hex : "transparent", color: PRIORITIES[p].text }}>
                  {PRIORITIES[p].label}<br /><span className="opacity-70">P{p}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("inc.description")}</label>
              <VoiceRecorder onTranscript={(t) => setForm((f) => ({ ...f, description: f.description ? `${f.description} ${t}` : t }))} />
            </div>
            <textarea data-testid="incident-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder={t("inc.descPh")} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("inc.assignVehicles")}</label>
            {vehicles.length > 6 && (
              <div className="mb-2 flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="vehicle-search" value={vehQ} onChange={(e) => setVehQ(e.target.value)} placeholder={t("inc.vehSearchPh")}
                    className="w-full rounded-lg border border-slate-200 py-1.5 pl-8 pr-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
                {vehLocations.length > 0 && (
                  <select data-testid="vehicle-loc-filter" value={vehLoc} onChange={(e) => setVehLoc(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 outline-none focus:border-blue-500">
                    <option value="">{t("inc.allLocations")}</option>
                    {vehLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                )}
              </div>
            )}
            <div className="grid max-h-56 grid-cols-2 gap-2 overflow-y-auto">
              {shownVehicles.length === 0 && <div className="col-span-2 py-4 text-center text-xs text-slate-400">{t("inc.noVehiclesFound")}</div>}
              {shownVehicles.map((v) => (
                <button key={v.id} type="button" data-testid={`assign-vehicle-${v.id}`} onClick={() => toggleVehicle(v.id)}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-all ${selectedVehicles.includes(v.id) ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                  <Truck style={{ width: 15, height: 15 }} className="shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <div className="font-mono text-xs font-semibold">{v.callsign}</div>
                    {v.location_id && locMap[v.location_id] && <div className="truncate text-[10px] text-slate-400">{locMap[v.location_id]}</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <button data-testid="submit-incident" type="submit" disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("inc.createBtn")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Incidents() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState((user.location_ids && user.location_ids[0]) || user.location_id || "");
  const [open, setOpen] = useState(!!location.state?.create);

  const lastRef = useRef("");
  const load = useCallback(async () => {
    const { data } = await api.get("/incidents?status=open");
    const serialized = JSON.stringify(data);
    if (serialized === lastRef.current) return;
    lastRef.current = serialized;
    setIncidents(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => { load(); }, 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/locations").then(({ data }) => setLocations(data || [])).catch(() => {}); }, []);

  const incidentLocIds = useCallback((inc) => {
    const ids = new Set();
    (inc.vehicles || []).forEach((v) => { if (v.location_id) ids.add(v.location_id); });
    if (inc.building?.location_id) ids.add(inc.building.location_id);
    return ids;
  }, []);
  const filtered = locFilter ? incidents.filter((inc) => incidentLocIds(inc).has(locFilter)) : incidents;

  const [flash, setFlash] = useState({});
  const clearFlash = useCallback((id) => setFlash((p) => { const n = { ...p }; delete n[id]; return n; }), []);
  useRealtime((msg) => {
    if (msg.type === "__ping") return;
    load();
    if (msg.type === "note_added" && msg.payload?.author_role === "voertuig" && msg.payload?.incident_id) {
      setFlash((p) => ({ ...p, [msg.payload.incident_id]: `Nieuwe kladblokregel · ${msg.payload.author || "voertuig"}` }));
    }
    if (msg.type === "vehicle_updated" && msg.payload?.id) {
      const pv = msg.payload;
      setIncidents((prev) => prev.some((i) => i.vehicles?.some((v) => v.id === pv.id && v.status !== pv.status))
        ? prev.map((i) => (i.vehicles?.some((v) => v.id === pv.id) ? { ...i, vehicles: i.vehicles.map((v) => (v.id === pv.id ? { ...v, status: pv.status, status_since: pv.status_since } : v)) } : i))
        : prev);
      const lbl = `Status ${msg.payload.callsign || "voertuig"} → ${VEHICLE_STATUSES[msg.payload.status]?.label || msg.payload.status}`;
      setFlash((p) => { const n = { ...p }; incidents.filter((i) => i.vehicles?.some((v) => v.id === msg.payload.id)).forEach((i) => { n[i.id] = lbl; }); return n; });
    }
  });

  const canCreate = ["beheerder", "meldkamer"].includes(user.role);

  const closeAll = async () => {
    const ok = await confirmDialog({ title: "Alle incidenten sluiten", message: `Weet je zeker dat je alle ${incidents.length} open incident(en) wilt sluiten? Alle gekoppelde voertuigen worden weer beschikbaar.`, danger: true, confirmText: "Alles sluiten" });
    if (!ok) return;
    try {
      const { data } = await api.post("/incidents/close-all");
      toast.success(`${data.closed} incident(en) gesloten · voertuigen beschikbaar`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sluiten mislukt");
    }
  };

  return (
    <div className="space-y-5" data-testid="incidents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("inc.title")}</h1>
          <p className="text-sm text-slate-500">{t("inc.openCount", { count: filtered.length })}{locFilter && locations.length ? ` · ${locations.find((l) => l.id === locFilter)?.name || ""}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {locations.length > 0 && (
            <select data-testid="incident-loc-filter" value={locFilter} onChange={(e) => setLocFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500">
              <option value="">Alle locaties</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          {canCreate && (
            <>
              {filtered.length > 0 && (
                <button data-testid="close-all-incidents-btn" onClick={closeAll}
                  className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-100">
                  <CheckCheck className="h-4 w-4" /> Alle incidenten sluiten
                </button>
              )}
              <button data-testid="new-incident-btn" onClick={() => setOpen(true)}
                className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">
                <Plus className="h-4 w-4" /> {t("inc.newIncident")}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">
            {locFilter && incidents.length > 0 ? "Geen open meldingen voor deze locatie." : t("inc.emptyList")}
          </div>
        )}
        {filtered.map((inc) => (
          <button key={inc.id} data-testid={`incident-item-${inc.id}`} onClick={() => { clearFlash(inc.id); navigate(`/incidenten/${inc.id}`); }}
            className={`group flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:shadow-md ${flash[inc.id] ? "flash-attention" : ""}`}>
            <div className="h-full w-1 shrink-0 self-stretch rounded-full" style={{ backgroundColor: PRIORITIES[inc.priority].hex }} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <PriorityBadge priority={inc.priority} />
                <span className="font-mono text-xs text-slate-400">{inc.ref}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">{inc.incident_type?.name}</span>
              </div>
              <div className="mt-1.5 truncate font-semibold text-slate-900">{inc.title}</div>
              {flash[inc.id] && <div data-testid={`flash-info-${inc.id}`} className="mt-1 inline-block rounded bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white">{flash[inc.id]}</div>}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Building2 style={{ width: 13, height: 13 }} />{inc.building?.name}</span>
                <span className="inline-flex flex-wrap items-center gap-1" data-testid={`inc-veh-statuses-${inc.id}`}>
                  <Truck style={{ width: 13, height: 13 }} />
                  {inc.vehicles?.length ? inc.vehicles.map((v) => { const s = VEHICLE_STATUSES[v.status] || {}; return (
                    <span key={v.id} data-testid={`inc-veh-status-${v.id}`} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold" style={{ backgroundColor: s.bg, color: s.text, border: `1px solid ${s.border || "#e2e8f0"}` }}>
                      <span className="font-mono">{v.callsign}</span><span className="opacity-70">·</span>{s.label || v.status}
                    </span>
                  ); }) : t("inc.none")}
                </span>
                <span>{timeAgo(inc.created_at)}</span>
              </div>
            </div>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-blue-600" />
          </button>
        ))}
      </div>

      <CreateDialog open={open} onClose={() => setOpen(false)} onCreated={() => {}} />
    </div>
  );
}
